import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import {
  AssessmentStatus,
  AssessmentListItem,
  AssessmentListData,
  AssessmentDetail,
  AssessmentDiscipline,
  ApiAssessmentDiscipline,
  ReopenHistoryEntry
} from '../../dtos/patientmr/assessments';
import { Offline } from '../offline/offlineServices';
import { OfflineMode, runWithOfflineBranch } from '../offline/offlineMode';
import { isOfflineError, isOnline } from '../offline/network';
import { ulid } from '../offline/ids';
import type { LocalAssessment } from '../offline/repos/assessmentRepo';

// Re-export types for backward compatibility
export {
  AssessmentStatus,
  type AssessmentListItem,
  type AssessmentListData,
  type AssessmentDetail,
  type ReopenHistoryEntry
};

// Re-export discipline enums from DTO (single source of truth)
export { AssessmentDiscipline, ApiAssessmentDiscipline };

/**
 * Utility function to extract provider name from provider object or string
 * @param provider - The provider data (string or object)
 * @returns Formatted provider name string
 */
function extractProviderName(provider: any): string {
  if (typeof provider === 'string') {
    return provider;
  }

  if (provider && typeof provider === 'object') {
    // Try different combinations of name fields
    if (provider.firstName && provider.lastName) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    if (provider.name) {
      return provider.name;
    }
    if (provider.email) {
      return provider.email;
    }
    if (provider.displayName) {
      return provider.displayName;
    }
  }

  return 'Unknown Provider';
}

/**
 * Map backend assessment type to frontend values
 * @param backendType - Backend assessment type (START_OF_CARE, REASSESSMENT, END_OF_CARE)
 * @returns Frontend assessment type (initial, recertification, discharge)
 */
function mapBackendToFrontendAssessmentType(backendType: string | undefined): string | undefined {
  if (!backendType) return undefined;

  const mapping: Record<string, string> = {
    'START_OF_CARE': 'initial',
    'REASSESSMENT': 'recertification',
    'END_OF_CARE': 'discharge'
  };

  return mapping[backendType] || backendType;
}

/**
 * Map frontend assessment type to backend values
 * @param frontendType - Frontend assessment type (initial, recertification, discharge)
 * @returns Backend assessment type (START_OF_CARE, REASSESSMENT, END_OF_CARE)
 */
function mapFrontendToBackendAssessmentType(frontendType: string | undefined): string | undefined {
  if (!frontendType) return undefined;

  const mapping: Record<string, string> = {
    'initial': 'START_OF_CARE',
    'recertification': 'REASSESSMENT',
    'discharge': 'END_OF_CARE'
  };

  return mapping[frontendType] || frontendType;
}

/**
 * Service for managing patient assessments with backend API
 */
export class AssessmentsService {
  /**
   * Fetch all assessments for a patient
   * @param patientId - The ID of the patient
   * @param queryParams - Optional query parameters for filtering assessments (e.g., { category: 'oasis', visitId: 'visit123', limit: '6', paginationToken: 'token' })
   * @returns Promise with the list of assessments and pagination info
   */
  static async getAssessments(patientId: string, queryParams: Record<string, string> = {}): Promise<{ assessments: AssessmentListItem[], hasMore: boolean, paginationToken?: string }> {
    // Phase 16C: offline on an enrolled phone, the assessments kept on this device
    if (OfflineMode.enabled() && !isOnline()) {
      const local = (await Offline.assessments.listForPatient(patientId))
        .filter((a) => !queryParams.visitId || a.visitId === queryParams.visitId);
      return {
        assessments: this.formatAssessmentList(local.map((a) => ({ ...a.body, assessmentId: a.assessmentId, status: a.status, date: a.updatedAt }))),
        hasMore: false,
      };
    }
    try {
      const response: ApiResponse<AssessmentListData> = await apiClient.get(
        `/patientmr/${patientId}/assessments`,
        queryParams
      );

      if (response.success && response.data) {
        const assessments = response.data.assessments || [];
        const paginationInfo = response.data.paginationInfo || response.data.pagination || {};

        return {
          assessments: this.formatAssessmentList(assessments),
          hasMore: paginationInfo.hasMoreItems || paginationInfo.hasMore || false,
          paginationToken: paginationInfo.nextToken || paginationInfo.paginationToken
        };
      }

      throw new Error(response.error?.message || 'Failed to fetch assessments');
    } catch (error) {
      console.error(`Error fetching assessments for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch assessments for a specific visit
   * @param patientId - The ID of the patient
   * @param visitId - The ID of the visit
   * @returns Promise with the list of assessments for the visit and pagination info
   */
  static async getAssessmentsByVisit(patientId: string, visitId: string): Promise<{ assessments: AssessmentListItem[], hasMore: boolean, paginationToken?: string }> {
    return runWithOfflineBranch({
      visitId,
      // Phase 16C: assessments started or edited on this device
      local: async () => {
        const local = await Offline.local.listAssessments(visitId);
        return {
          assessments: this.formatAssessmentList(local.map((a) => ({ ...a.body, assessmentId: a.assessmentId, status: a.status, date: a.updatedAt, syncState: a.syncState }))),
          hasMore: false,
        };
      },
      online: () => this.getAssessments(patientId, { visitId }),
    });
  }

  /** Phase 16C: the device copy of an assessment, in the API shape. */
  private static localDetail(a: LocalAssessment): AssessmentDetail {
    return this.formatAssessment({ ...a.body, assessmentId: a.assessmentId, status: a.status, editVersion: a.serverVersion });
  }

  /** Phase 16C: keep an online assessment on an enrolled device so autosave works without signal. */
  private static async cacheOnline(patientId: string, visitId: string | undefined, detail: AssessmentDetail, apiData: Record<string, unknown>): Promise<void> {
    if (!OfflineMode.enabled() || !detail.assessmentId) return;
    try {
      const existing = await Offline.assessments.get(detail.assessmentId);
      if (existing && existing.syncState !== 'synced') return;
      const now = new Date().toISOString();
      await Offline.assessments.put({
        assessmentId: detail.assessmentId,
        visitId: visitId || existing?.visitId || '',
        patientId,
        body: { ...(existing?.body || {}), ...apiData },
        status: detail.status === AssessmentStatus.DRAFT ? 'DRAFT' : 'COMPLETED',
        serverVersion: detail.editVersion,
        syncState: 'synced',
        origin: existing?.origin || 'server',
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      });
    } catch {
      // cache only
    }
  }

  /**
   * Get a specific assessment's details
   * @param patientId - The ID of the patient
   * @param assessmentId - The ID of the assessment
   * @returns Promise with the assessment details
   */
  static async getAssessmentDetails(patientId: string, assessmentId: string, version?: number): Promise<AssessmentDetail> {
    // Phase 16C: the device copy when offline or while it has unsynced changes
    if (OfflineMode.enabled() && version === undefined) {
      const local = await Offline.assessments.get(assessmentId).catch(() => undefined);
      if (local && (!isOnline() || local.syncState !== 'synced')) return this.localDetail(local);
    }
    try {
      // `?version=n` returns a read-only snapshot of an earlier version (Phase 4)
      const response: ApiResponse<AssessmentDetail> = await apiClient.get(
        `/patientmr/${patientId}/assessments/${assessmentId}`,
        version !== undefined ? { version } : undefined
      );

      if (response.success && response.data) {
        return this.formatAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to fetch assessment details');
    } catch (error) {
      console.error(`Error fetching assessment details for patient ${patientId}, assessment ${assessmentId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new assessment
   * @param patientId - The ID of the patient
   * @param visitId - The ID of the visit
   * @param assessmentData - The assessment data to create
   * @returns Promise with the created assessment
   */
  static async createAssessment(
    patientId: string,
    visitId: string,
    assessmentData: AssessmentDetail,
    /** Phase 19 F14: a reassessment / discharge assessment starts from a prior COMPLETE assessment */
    options: { copyFromId?: string } = {}
  ): Promise<AssessmentDetail> {
    // Convert UI discipline to API discipline before sending to API
    // Phase 16C: client ULID as the assessment id and Idempotency-Key
    const assessmentId = assessmentData.assessmentId || ulid();
    const apiData = {
      ...assessmentData,
      discipline: this.mapUiDisciplineToApiDiscipline(assessmentData.discipline),
      ...(options.copyFromId ? { copyFromId: options.copyFromId } : {})
    };
    delete (apiData as { assessmentId?: string }).assessmentId; // 16B: the id defaults to the Idempotency-Key
    return runWithOfflineBranch({
      visitId,
      local: async () => this.localDetail(await Offline.local.createAssessment(patientId, visitId, { ...apiData }, assessmentId)),
      online: async () => {
        try {
          const response: ApiResponse<AssessmentDetail> = await apiClient.post(
            `/patientmr/${patientId}/visits/${visitId}/assessments`,
            apiData,
            { idempotencyKey: assessmentId }
          );
          if (response.success && response.data) {
            const detail = this.formatAssessment(response.data);
            await this.cacheOnline(patientId, visitId, detail, { ...apiData });
            return detail;
          }
          throw new Error(response.error?.message || 'Failed to create assessment');
        } catch (error) {
          if (!isOfflineError(error)) console.error(`Error creating assessment for patient ${patientId}, visit ${visitId}`);
          throw error;
        }
      },
    });
  }

  /**
   * Update an existing assessment
   * @param patientId - The ID of the patient
   * @param assessmentId - The ID of the assessment
   * @param assessmentData - The assessment data to update
   * @returns Promise with the updated assessment
   */
  static async updateAssessment(
    patientId: string,
    assessmentId: string,
    assessmentData: AssessmentDetail
  ): Promise<AssessmentDetail> {
    // Convert UI discipline to API discipline before sending to API
    const apiData = {
      ...assessmentData,
      discipline: this.mapUiDisciplineToApiDiscipline(assessmentData.discipline)
    };
    const cached = OfflineMode.enabled() ? await Offline.assessments.get(assessmentId).catch(() => undefined) : undefined;
    return runWithOfflineBranch({
      visitId: cached?.visitId,
      local: async () => this.localDetail(await Offline.local.updateAssessment(patientId, assessmentId, { ...apiData })),
      online: async () => {
        try {
          const response: ApiResponse<AssessmentDetail> = await apiClient.put(
            `/patientmr/${patientId}/assessments/${assessmentId}`,
            apiData
          );
          if (response.success && response.data) {
            const detail = this.formatAssessment(response.data);
            await this.cacheOnline(patientId, undefined, detail, { ...apiData });
            return detail;
          }
          throw new Error(response.error?.message || 'Failed to update assessment');
        } catch (error) {
          if (!isOfflineError(error)) console.error(`Error updating assessment ${assessmentId} for patient ${patientId}`);
          throw error;
        }
      },
    });
  }


  /**
   * Map form data to backend assessment data structure
   * @param formData - The form data from the UI
   * @param discipline - The discipline for the assessment
   * @returns Mapped assessment data for the backend
   */
  static mapFormDataToAssessment(
    formData: Record<string, any>,
    discipline: AssessmentDiscipline,
    provider: any
  ): AssessmentDetail {
    // Ensure provider is a string
    const providerName = extractProviderName(provider);

    // Extract assessmentType BEFORE stripping — needed as root-level field for the backend.
    // formConfig.assessmentType holds frontend values ('initial', 'recertification', 'discharge');
    // map to backend canonical value and send as root-level assessmentType.
    const mappedFormData = { ...formData };
    const uiAssessmentType = mappedFormData.formConfig?.assessmentType;
    const backendAssessmentType = uiAssessmentType
      ? mapFrontendToBackendAssessmentType(uiAssessmentType)
      : undefined;

    if (mappedFormData.formConfig?.assessmentType !== undefined) {
      mappedFormData.formConfig = { ...mappedFormData.formConfig };
      delete mappedFormData.formConfig.assessmentType;
    }

    return {
      provider: providerName,
      discipline,
      assessmentType: backendAssessmentType,
      data: mappedFormData
    };
  }

  /**
   * Map backend assessment data to form data structure
   * @param assessmentDetail - The assessment detail from the backend
   * @returns Mapped form data for the UI
   */
  static mapAssessmentToFormData(assessmentDetail: AssessmentDetail): Record<string, any> {
    const mappedData: Record<string, any> = {
      ...assessmentDetail.data,
      provider: assessmentDetail.provider,
      discipline: assessmentDetail.discipline,
      status: assessmentDetail.status
    };

    // Reconstruct formConfig.assessmentType from root assessmentType for UI form logic.
    // formConfig.assessmentType is UI-only (values: 'initial', 'recertification', 'discharge')
    // and is never stored in DynamoDB — root assessmentType is the single source of truth.
    if (assessmentDetail.assessmentType) {
      const frontendType = mapBackendToFrontendAssessmentType(assessmentDetail.assessmentType);
      if (frontendType) {
        mappedData.formConfig = { ...mappedData.formConfig, assessmentType: frontendType };
      }
    }

    return mappedData;
  }

  /**
   * Map UI discipline to API discipline
   * @param uiDiscipline - The UI-facing discipline enum value
   * @returns API-facing discipline enum value
   */
  static mapUiDisciplineToApiDiscipline(uiDiscipline: AssessmentDiscipline): ApiAssessmentDiscipline {
    switch (uiDiscipline) {
      case AssessmentDiscipline.RN:
        return ApiAssessmentDiscipline.RN;
      case AssessmentDiscipline.PT:
        return ApiAssessmentDiscipline.PT;
      case AssessmentDiscipline.OT:
        return ApiAssessmentDiscipline.OT;
      case AssessmentDiscipline.ST:
        return ApiAssessmentDiscipline.ST;
      case AssessmentDiscipline.MSW:
        return ApiAssessmentDiscipline.MSW;
      case AssessmentDiscipline.HHA:
        return ApiAssessmentDiscipline.HHA;
      default:
        return ApiAssessmentDiscipline.RN; // Default fallback
    }
  }

  /**
   * Map API discipline to UI discipline
   * @param apiDiscipline - The API-facing discipline enum value
   * @returns UI-facing discipline enum value
   */
  static mapApiDisciplineToUiDiscipline(apiDiscipline: ApiAssessmentDiscipline | string): AssessmentDiscipline {
    // Handle string values from API
    const disciplineStr = typeof apiDiscipline === 'string' ? apiDiscipline : String(apiDiscipline);

    switch (disciplineStr) {
      case 'RN':
      case ApiAssessmentDiscipline.RN:
        return AssessmentDiscipline.RN;
      case 'PT':
      case 'PHYSICAL_THERAPY':
      case ApiAssessmentDiscipline.PT:
        return AssessmentDiscipline.PT;
      case 'OT':
      case 'OCCUPATIONAL_THERAPY':
      case ApiAssessmentDiscipline.OT:
        return AssessmentDiscipline.OT;
      case 'ST':
      case 'SPEECH_THERAPY':
      case ApiAssessmentDiscipline.ST:
        return AssessmentDiscipline.ST;
      case 'MSW':
      case ApiAssessmentDiscipline.MSW:
        return AssessmentDiscipline.MSW;
      case 'HHA':
      case ApiAssessmentDiscipline.HHA:
        return AssessmentDiscipline.HHA;
      default:
        return AssessmentDiscipline.RN; // Default fallback
    }
  }

  /**
   * Helper function to format assessment data from the API response
   * @param assessment Assessment data from the API
   * @returns Formatted AssessmentDetail object
   */
  static formatAssessment(assessment: any): AssessmentDetail {
    // Map API discipline to UI discipline
    const apiDiscipline = assessment.discipline as ApiAssessmentDiscipline;
    const uiDiscipline = this.mapApiDisciplineToUiDiscipline(apiDiscipline);

    return {
      assessmentId: assessment.assessmentId,
      assessmentType: assessment.assessmentType,
      provider: extractProviderName(assessment.provider),
      discipline: uiDiscipline,
      status: assessment.status || AssessmentStatus.DRAFT,
      data: assessment.data || {},
      version: typeof assessment.version === 'number' ? assessment.version : undefined,
      editVersion: typeof assessment.editVersion === 'number' ? assessment.editVersion : undefined,
      reopenHistory: Array.isArray(assessment.reopenHistory) ? assessment.reopenHistory : undefined
    };
  }

  /**
   * Helper function to format an array of assessments from the API
   * @param assessments Array of assessments from the API
   * @returns Array of formatted AssessmentListItem objects
   */
  static formatAssessmentList(assessments: any[]): AssessmentListItem[] {
    return assessments.map(assessment => {
      // Map API discipline to UI discipline
      const apiDiscipline = assessment.discipline as ApiAssessmentDiscipline;
      const uiDiscipline = this.mapApiDisciplineToUiDiscipline(apiDiscipline);

      // Ensure providerName is a string - handle cases where provider might be an object
      const providerName = extractProviderName(assessment.provider);

      // Root assessmentType is the single source of truth
      const assessmentType = assessment.assessmentType;

      return {
        assessmentId: assessment.assessmentId,
        date: assessment.date || new Date().toLocaleString(),
        status: assessment.status || AssessmentStatus.DRAFT,
        discipline: uiDiscipline,
        providerName: providerName,
        assessmentType: assessmentType
      };
    });
  }

  /**
   * Finalize an assessment
   * @param patientId The patient ID
   * @param assessmentId The assessment ID to finalize
   * @returns A Promise that resolves when the assessment is finalized
   */
  static async finalizeAssessment(patientId: string, assessmentId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/assessments/${assessmentId}/finalize`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to finalize assessment');
      }
    } catch (error) {
      console.error(`Error finalizing assessment ${assessmentId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Phase 19 F16: printable assessment (optionally a past version); returns a 5-minute link.
   */
  static async getAssessmentPdfUrl(patientId: string, assessmentId: string, version?: number): Promise<{ url: string; fileName: string }> {
    const query = version ? `?version=${version}` : '';
    const response: ApiResponse<{ url: string; fileName: string }> = await apiClient.get(`/patientmr/${patientId}/assessments/${assessmentId}/pdf${query}`);
    if (response.success && response.data?.url) return response.data;
    throw new Error(response.error?.message || 'Failed to prepare the assessment PDF');
  }

  /**
   * Submit an assessment
   * @param patientId The patient ID
   * @param assessmentId The assessment ID to submit
   * @returns A Promise that resolves when the assessment is submitted
   */
  static async submitAssessment(patientId: string, assessmentId: string): Promise<void> {
    // Phase 16C: offline, the clinician's submit is queued as ASSESSMENT_COMPLETE (16B dispatch)
    const cached = OfflineMode.enabled() ? await Offline.assessments.get(assessmentId).catch(() => undefined) : undefined;
    return runWithOfflineBranch({
      visitId: cached?.visitId,
      local: async () => { await Offline.local.completeAssessment(patientId, assessmentId); },
      online: async () => {
        try {
          const response: ApiResponse<void> = await apiClient.put(
            `/patientmr/${patientId}/assessments/${assessmentId}/submit`
          );
          if (!response.success) {
            throw new Error(response.error?.message || 'Failed to submit assessment');
          }
        } catch (error) {
          if (!isOfflineError(error)) console.error(`Error submitting assessment ${assessmentId} for patient ${patientId}`);
          throw error;
        }
      },
    });
  }

  /**
   * Reopen an assessment (SUBMITTED/QA/COMPLETE -> DRAFT). The server requires a reason of at
   * least 5 characters, records it in `reopenHistory` and bumps `version`.
   * @param patientId The patient ID
   * @param assessmentId The assessment ID to reopen
   * @param reason Why the record is being reopened or rejected
   * @returns A Promise that resolves when the assessment is reopened
   */
  static async reopenAssessment(patientId: string, assessmentId: string, reason: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/assessments/${assessmentId}/reopen`,
        { reason }
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reopen assessment');
      }
    } catch (error) {
      console.error(`Error reopening assessment ${assessmentId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Start QA for an assessment
   * @param patientId The patient ID
   * @param assessmentId The assessment ID to start QA for
   * @returns A Promise that resolves when QA is started for the assessment
   */
  static async startQAAssessment(patientId: string, assessmentId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/assessments/${assessmentId}/startQA`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to start QA for assessment');
      }
    } catch (error) {
      console.error(`Error starting QA for assessment ${assessmentId} for patient ${patientId}:`, error);
      throw error;
    }
  }
}
